import React, { useEffect, useState } from "react";
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Button, 
    useTheme, 
    useMediaQuery,
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Skeleton
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import FMBlogo from "../../../assests/FMBlogo.png";
import Logo from "../../../assests/logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import QueueManager from "../../../utils/QueueManager";
import Cookies from "js-cookie";
import { getUserFrontendUrl, getFrontendUrl } from "../../../config/config";
import { getMenus } from "../../../api/api";

const ThankYou = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const open = Boolean(anchorEl);
    const {
        queueNumber = "00",
        selectedOptions = [],
        ticketCode = "DEMO",
        isDemoMode = true,
        isLost = false,
    } = location.state || {};

    // Get ticket details from location state first (since cookie may be cleared), then cookie
    const ticketDataFromState = location.state?.ticketData;
    const ticketDetailsFromCookie = Cookies.get("ticketDetails");
    const ticketData = ticketDataFromState || (ticketDetailsFromCookie ? JSON.parse(ticketDetailsFromCookie) : null);
    const branchId = ticketData?.client?.id; // Extract branch ID from ticket data
    
    // Get logo filename from database and construct the URL
    const logoFilename = ticketData?.client?.logo;
    const logoUrl = logoFilename 
        ? getFrontendUrl(`/storage/logos/${logoFilename}`)
        : FMBlogo; // Fallback to default logo if not available

    // Preload logo for faster loading (only for remote URLs, not local imports)
    useEffect(() => {
        if (logoUrl && logoFilename && logoUrl.startsWith('http')) {
            try {
                // Create link element for preloading
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = logoUrl;
                link.fetchPriority = 'high';
                document.head.appendChild(link);

                // Also preconnect to the domain for faster connection
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = new URL(logoUrl).origin;
                document.head.appendChild(preconnect);

                return () => {
                    // Cleanup on unmount
                    if (document.head.contains(link)) {
                        document.head.removeChild(link);
                    }
                    if (document.head.contains(preconnect)) {
                        document.head.removeChild(preconnect);
                    }
                };
            } catch (error) {
                console.warn('Error preloading logo:', error);
            }
        }
    }, [logoUrl, logoFilename]);

    // Reset logo loaded state when logoUrl changes and add timeout fallback
    useEffect(() => {
        setLogoLoaded(false);
        
        // Fallback: if logo doesn't load within 5 seconds, show it anyway
        const timeout = setTimeout(() => {
            console.warn('Logo loading timeout, showing image anyway');
            setLogoLoaded(true);
        }, 5000);
        
        return () => clearTimeout(timeout);
    }, [logoUrl]);

    const handleBackToHome = () => {
        navigate("/getticket/" + ticketCode);
    };

    // Fix scrolling issue by overriding global overflow hidden and scroll to top
    useEffect(() => {
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Save original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalBodyHeight = bodyElement.style.height;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalHtmlHeight = htmlElement.style.height;
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add CSS class
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        // Scroll to top immediately when component mounts
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (rootElement) {
            rootElement.scrollTop = 0;
        }
        
        // Also scroll to top after a small delay to ensure it works
        // This handles cases where content loads after initial render
        const scrollTimeout = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (rootElement) {
                rootElement.scrollTop = 0;
            }
        }, 100);
        
        // Also scroll after layout is complete
        const layoutTimeout = setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (rootElement) {
                rootElement.scrollTop = 0;
            }
        }, 300);
        
        return () => {
            clearTimeout(scrollTimeout);
            clearTimeout(layoutTimeout);
            // Restore original styles
            bodyElement.style.overflow = originalBodyOverflow || 'hidden';
            bodyElement.style.height = originalBodyHeight || '';
            htmlElement.style.overflow = originalHtmlOverflow || 'hidden';
            htmlElement.style.height = originalHtmlHeight || '';
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow || 'hidden';
                rootElement.style.height = originalRootHeight || '100%';
            }
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    // Fetch menu items when branchId is available
    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!branchId) {
                return;
            }

            try {
                const response = await getMenus(branchId);
                if (response.success) {
                    setMenuItems(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching menu items:", error);
                // Set empty array on error to prevent issues
                setMenuItems([]);
            }
        };

        fetchMenuItems();
    }, [branchId]);

    // Clear queue data when reaching thank you page
    useEffect(() => {
        const clearQueueData = () => {
            if (ticketCode && ticketCode !== "DEMO") {
                QueueManager.clearQueue(ticketCode);
                console.log("Queue data cleared after thank you page");
            }
        };

        clearQueueData();
    }, [ticketCode]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 2,
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Logo"
                                style={{
                                    height: "24px",
                                    marginRight: "8px",
                                    filter: "brightness(0) invert(1)", // Make logo white
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ flexGrow: 1 }}
                        >
                        </Typography>

                        <IconButton
                            size="large"
                            color="inherit"
                            aria-label="menu"
                            sx={{ ml: 2 }}
                            onClick={handleClick}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Top menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {/* Default HOME menu item */}
                            <MenuItem onClick={handleClose}>
                                <a
                                    href={getUserFrontendUrl(`/get-ticket/${ticketData?.ticket_code || ticketCode || "DEMO"}`)}
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    HOME
                                </a>
                            </MenuItem>
                            {/* Dynamic menu items from database */}
                            {menuItems.map((menu) => {
                                if (!menu.menu_link) return null;
                                
                                const isExternalUrl = menu.menu_link.startsWith('http://') || menu.menu_link.startsWith('https://');
                                const href = isExternalUrl 
                                    ? menu.menu_link 
                                    : getUserFrontendUrl(menu.menu_link);
                                
                                return (
                                    <MenuItem key={menu.id} onClick={handleClose}>
                                        <a
                                            href={href}
                                            target={isExternalUrl ? "_blank" : undefined}
                                            rel={isExternalUrl ? "noopener noreferrer" : "noopener noreferrer"}
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                        >
                                            {menu.menu_title}
                                        </a>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </Toolbar>
                </AppBar>
            </Box>

            <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                minHeight: "100vh",
                px: { xs: 2, sm: 3 },
                py: { xs: 3, sm: 4 },
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                boxSizing: "border-box"
            }}
        >
            <Card
                sx={{
                    maxWidth: { xs: "100%", sm: 600 },
                    width: "100%",
                    textAlign: "center",
                    boxShadow: 3,
                    mb: { xs: 3, sm: 4 }
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                    <Box mb={{ xs: 2, sm: 3 }} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: isMobile ? 60 : 80, position: "relative" }}>
                        {!logoLoaded && (
                            <Skeleton 
                                variant="rectangular" 
                                width={isMobile ? 150 : 200} 
                                height={isMobile ? 60 : 80} 
                                sx={{ borderRadius: 1, position: "absolute" }}
                            />
                        )}
                        <img
                            src={logoUrl}
                            alt="FMB Logo"
                            loading="eager"
                            fetchPriority="high"
                            style={{
                                width: isMobile ? 150 : 200,
                                maxWidth: "100%",
                                height: "auto",
                                opacity: logoLoaded ? 1 : 0,
                                transition: "opacity 0.3s ease-in-out",
                                margin: "0 auto",
                                position: "relative"
                            }}
                            onLoad={() => {
                                console.log('Logo loaded successfully');
                                setLogoLoaded(true);
                            }}
                            onError={(e) => {
                                console.error('Logo failed to load:', logoUrl, e);
                                // Show fallback logo if error
                                if (logoUrl !== FMBlogo) {
                                    e.target.src = FMBlogo;
                                }
                                setLogoLoaded(true);
                            }}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        mb={{ xs: 2, sm: 3 }}
                    >
                        {isLost ? (
                            <WarningIcon
                                sx={{
                                    fontSize: { xs: 60, sm: 80 },
                                    color: "#ff9800",
                                }}
                            />
                        ) : (
                            <CheckCircleIcon
                                sx={{
                                    fontSize: { xs: 60, sm: 80 },
                                    color: "#4caf50",
                                }}
                            />
                        )}
                    </Box>

                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{ 
                            fontWeight: "bold", 
                            color: isLost ? "#ff9800" : "#2e7d32",
                            fontSize: { xs: "1.75rem", sm: "2.125rem" },
                            px: { xs: 1, sm: 0 }
                        }}
                    >
                        {isLost ? "Beklager, men vi fant deg ikke når det var din tur" : "Takk for besøket!"}
                    </Typography>

                    {isLost && (
                        <Typography
                            variant="body1"
                            sx={{ 
                                mb: { xs: 2, sm: 3 },
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                px: { xs: 1, sm: 0 },
                                lineHeight: { xs: 1.5, sm: 1.6 },
                                color: "#666",
                                fontStyle: "italic"
                            }}
                        >
                            Vi beklager at vi ikke kunne nå deg i køen. Vennligst ta en ny kø-nummer hvis du fortsatt trenger hjelp.
                        </Typography>
                    )}

                    {selectedOptions && selectedOptions.length > 0 && !isLost && (
                        <Box mb={{ xs: 2, sm: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ 
                                    fontWeight: "bold",
                                    fontSize: { xs: "1rem", sm: "1.25rem" },
                                    px: { xs: 1, sm: 0 }
                                }}
                            >
                                Tjenester du fikk hjelp med:
                            </Typography>
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                justifyContent="center"
                                gap={{ xs: 0.5, sm: 1 }}
                            >
                                {selectedOptions.map((option, index) => (
                                    <Typography
                                        key={index}
                                        variant="body1"
                                        sx={{
                                            backgroundColor: "#e8f5e8",
                                            color: "#2e7d32",
                                            px: { xs: 1.5, sm: 2 },
                                            py: { xs: 0.75, sm: 1 },
                                            borderRadius: 2,
                                            fontWeight: "medium",
                                            fontSize: { xs: "0.875rem", sm: "1rem" },
                                            mb: { xs: 0.5, sm: 0 }
                                        }}
                                    >
                                        {typeof option === "object"
                                            ? option.service_name
                                            : option}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {!isLost && (
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ 
                                mb: { xs: 3, sm: 4 },
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                px: { xs: 1, sm: 0 },
                                lineHeight: { xs: 1.5, sm: 1.6 }
                            }}
                        >
                            Vi håper du fikk god hjelp og er fornøyd med tjenesten
                            vår. Velkommen tilbake!
                        </Typography>
                    )}

                    {/* <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleBackToHome}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: "1.1rem",
                            textTransform: "none",
                        }}
                    >
                        Tilbake til start
                    </Button> */}
                </CardContent>
            </Card>

            <Box mt={{ xs: 3, sm: 4 }} textAlign="center" mb={2}>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                    easyQue (c) by Digifront
                </Typography>
                <Typography 
                    variant="body2" 
                    color="primary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                    sales@digifront.biz
                </Typography>
            </Box>
        </Box>
        </>
    );
};

export default ThankYou;
